import { createLegacyEnvConfig, Environment, ServicesOrchestrator } from "@pandas/constants";
import { z } from "zod";
import type { ClientType } from "@/lib/ably/vision";

const parseList = (value: string) =>
	value
		.split(",")
		.map((entry) => entry.trim())
		.filter(Boolean);

const parseOptionalBoolean = (value: string | undefined) => {
	if (value === "true") return true;
	if (value === "false") return false;
	return undefined;
};

const parseOptionalNumber = (value: string | undefined) => {
	if (!value) return undefined;
	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

export const appEnvSchema = z.object({
	VITE_APP_NAME: z.string().default("vision-ai"),
	VITE_APP_VERSION: z.string().default("0.0.0"),
	VITE_APP_ENV: z.enum(Environment).optional(),
	VITE_SIMPLELOCALIZE_BASE_URL: z.string().default("https://cdn.simplelocalize.io"),
	VITE_SIMPLELOCALIZE_PROJECT_TOKEN: z.string(),
	VITE_SIMPLELOCALIZE_ENV: z.string(),
	VITE_CONTENT_URL: z.string().default(""),
	VITE_NETWORK_MANAGER_ENABLED: z.string().default("false"),
	VITE_NETWORK_MANAGER_TIMEOUT: z.string().default("5"),
	VITE_ORIENTATION_EVENT_INTERVAL: z.string().default("200"),
	VITE_GRADE_RESPONSE_TIMEOUT: z.string().default("120000"),
	VITE_ABLY_CONFIRM_SEND_TIMEOUT_MS: z.string().default("3000"),
	VITE_ABLY_TOXIPROXY_ENABLED: z.string().default("false"),
	VITE_ABLY_TOXIPROXY_CLIENT_IDS: z.string().default(""),
	VITE_ABLY_TOXIPROXY_ENDPOINT: z.string().optional(),
	VITE_ABLY_TOXIPROXY_PORT: z.string().optional(),
	VITE_ABLY_TOXIPROXY_TLS: z.string().optional(),
	VITE_ABLY_TOXIPROXY_TLS_PORT: z.string().optional(),
	VITE_ABLY_TOXIPROXY_DISABLE_CONNECTIVITY_CHECK: z.string().optional(),
	VITE_AUTH0_DOMAIN: z.string(),
	VITE_AUTH0_CLIENT_ID: z.string(),
	VITE_AUTH0_AUDIENCE: z.string().optional(),
});

const commonEnv = createLegacyEnvConfig(import.meta.env);
const appEnv = appEnvSchema.parse(import.meta.env);

const orchestrator = new ServicesOrchestrator({ env: commonEnv });

export const servicesOrchestratorValues = {
	app: {
		name: appEnv.VITE_APP_NAME,
		version: appEnv.VITE_APP_VERSION,
		env: appEnv.VITE_APP_ENV ?? commonEnv.COMMON_ENV_ENVIRONMENT,
		simpleLocalizeBaseUrl: appEnv.VITE_SIMPLELOCALIZE_BASE_URL,
		networkManager: {
			enabled: appEnv.VITE_NETWORK_MANAGER_ENABLED === "true",
			timeoutSeconds: Number(appEnv.VITE_NETWORK_MANAGER_TIMEOUT),
		},
		orientationEventIntervalMs: Number(appEnv.VITE_ORIENTATION_EVENT_INTERVAL),
		gradeResponseTimeoutMs: Number(appEnv.VITE_GRADE_RESPONSE_TIMEOUT),
		ably: {
			confirmSendTimeoutMs: Number(appEnv.VITE_ABLY_CONFIRM_SEND_TIMEOUT_MS),
			toxiproxy: {
				enabled: appEnv.VITE_ABLY_TOXIPROXY_ENABLED === "true",
				clientIds: parseList(appEnv.VITE_ABLY_TOXIPROXY_CLIENT_IDS) as ClientType[],
				endpoint: appEnv.VITE_ABLY_TOXIPROXY_ENDPOINT?.trim() || undefined,
				port: parseOptionalNumber(appEnv.VITE_ABLY_TOXIPROXY_PORT),
				tls: parseOptionalBoolean(appEnv.VITE_ABLY_TOXIPROXY_TLS),
				tlsPort: parseOptionalNumber(appEnv.VITE_ABLY_TOXIPROXY_TLS_PORT),
				disableConnectivityCheck: parseOptionalBoolean(appEnv.VITE_ABLY_TOXIPROXY_DISABLE_CONNECTIVITY_CHECK),
			},
		},
	},
	auth0: {
		domain: appEnv.VITE_AUTH0_DOMAIN,
		clientId: appEnv.VITE_AUTH0_CLIENT_ID,
		audience: appEnv.VITE_AUTH0_AUDIENCE,
	},
	environment: orchestrator.environment,
	isProduction: orchestrator.isProduction,
	isUat: orchestrator.isUat,
	isDevelopment: orchestrator.isDevelopment,
	urls: {
		...orchestrator.urls,
		content: appEnv.VITE_CONTENT_URL,
	},
	credentials: {
		...orchestrator.credentials,
		ablyKey: commonEnv.COMMON_ENV_ABLY_API_KEY,
		posthogKey: commonEnv.COMMON_ENV_POSTHOG_KEY,
		posthogHost: commonEnv.COMMON_ENV_POSTHOG_HOST,
		simpleLocalizeProjectToken: appEnv.VITE_SIMPLELOCALIZE_PROJECT_TOKEN,
		simpleLocalizeEnvironment: appEnv.VITE_SIMPLELOCALIZE_ENV,
	},
} as const;
